import { NotificationIcon } from '@/src/assets/svgs'
import { useNotifications } from '@hook'
import { DropButton, Text } from 'grommet'
import { useNavigate } from 'react-router-dom'
import { resolveActionUrl } from './resolveActionUrl'
import type { AppNotification, NotificationAudience } from './types'
import { CATEGORY_LABELS } from './types'

const relativeTime = (iso: string) => {
  const mins = Math.max(0, Math.round((Date.now() - +new Date(iso)) / 60_000))
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

type Props = {
  audience: NotificationAudience
}

export const NotificationBell = ({ audience }: Props) => {
  const navigate = useNavigate()
  const { items, unreadCount, markRead, markAllRead } = useNotifications({ audience })
  const preview = items.slice(0, 8)
  const unread = unreadCount

  const openNotification = async (item: AppNotification) => {
    if (!item.readAt) {
      try {
        await markRead(item.id)
      } catch {
        // Optimistic rollback handled in the hook; still allow navigation.
      }
    }
    const href = resolveActionUrl(item)
    if (href) {
      navigate(href)
    }
  }

  return (
    <DropButton
      alignSelf="center"
      dropAlign={{ top: 'bottom', right: 'right' }}
      dropContent={
        <div className="w-[min(92vw,360px)] rounded-xl border border-[#E8E8E8] bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-[#EFEFEF] px-4 py-3">
            <Text size="small" weight="bold">
              Notifications {unread > 0 ? `(${unread})` : ''}
            </Text>
            <button
              className="text-xs text-primary disabled:opacity-40"
              disabled={unread === 0}
              onClick={() => markAllRead()}
              type="button"
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {preview.length === 0 ? (
              <p className="m-0 px-4 py-6 text-center text-sm text-[#888]">No notifications yet.</p>
            ) : (
              preview.map(item => (
                <button
                  className={`flex w-full flex-col gap-0.5 border-b border-[#F5F5F5] px-4 py-3 text-left hover:bg-[#FAFAFA] ${
                    item.readAt ? 'opacity-70' : ''
                  }`}
                  key={item.id}
                  onClick={() => openNotification(item)}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-black">{item.title}</span>
                    {!item.readAt ? <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" /> : null}
                  </div>
                  <span className="line-clamp-2 text-xs text-[#666]">{item.body}</span>
                  <span className="mt-1 text-[11px] text-[#999]">
                    {CATEGORY_LABELS[item.category] || item.category} · {relativeTime(item.createdAt)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      }
      dropProps={{ margin: { top: '8px' } }}
    >
      <span
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        className="relative inline-flex cursor-pointer items-center justify-center rounded-lg p-1.5 hover:bg-[#F0F0F0]"
        role="img"
      >
        <NotificationIcon height={22} width={22} />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </span>
    </DropButton>
  )
}
